import React, {useState, useEffect, useRef} from 'react';
import dateFormat from 'dateformat';

import getDaysListForMonth from '@ibibo/date-utils/src/getDaysListForMonth';
import modifyDateForDate from '@ibibo/date-utils/src/modifyDateForDate';
import getMonthDifferenceBetweenTwoDates from '@ibibo/date-utils/src/getMonthDifferenceBetweenTwoDates';

import ArrowLeftIcon from './ArrowLeftIcon';
import ArrowRightIcon from './ArrowRightIcon';
import * as S from './dcalendar.styles';

interface IMonthsViewProps {
  renderDateItem: Function;
  monthToBeDisplayed: number;
  yearToBeDisplayed: number;
}

export const MonthView = (props: IMonthsViewProps) => {
  const {
    renderDateItem,
    monthToBeDisplayed: monthToBeDisplayedFromProps,
    yearToBeDisplayed,
  } = props;

  const dateConfigForMonth = new Date();
  dateConfigForMonth.setDate(1);
  dateConfigForMonth.setMonth(monthToBeDisplayedFromProps);
  dateConfigForMonth.setFullYear(yearToBeDisplayed);

  const daysList = getDaysListForMonth(dateConfigForMonth);

  return (
    <>
      {daysList.map((item, i) =>
        renderDateItem({
          keyVal: `date${i}`,
          value: item,
          dateObj: (item && modifyDateForDate(dateConfigForMonth, item)) || null,
        }),
      )}
    </>
  );
};

function useCalendarView({
  dateConfigForMonthToBeDisplayedFromProps,
  minDateAllowed,
  maxDateAllowed,
}) {
  const [dateConfigForMonthToBeDisplayed, setDateConfigForMonthToBeDisplayed] = useState(
    dateConfigForMonthToBeDisplayedFromProps,
  );

  const hideLeftArrow =
    getMonthDifferenceBetweenTwoDates(minDateAllowed, dateConfigForMonthToBeDisplayed) === 0;

  const hideRightArrow =
    getMonthDifferenceBetweenTwoDates(dateConfigForMonthToBeDisplayed, maxDateAllowed) === 0;

  const leftArrowClickHandler = () => {
    if (hideLeftArrow) return;
    dateConfigForMonthToBeDisplayed.setMonth(dateConfigForMonthToBeDisplayed.getMonth() - 1);
    setDateConfigForMonthToBeDisplayed(new Date(dateConfigForMonthToBeDisplayed));
  };

  const rightArrowClickHandler = () => {
    if (hideRightArrow) return;
    dateConfigForMonthToBeDisplayed.setMonth(dateConfigForMonthToBeDisplayed.getMonth() + 1);
    setDateConfigForMonthToBeDisplayed(new Date(dateConfigForMonthToBeDisplayed));
  };

  return [
    hideLeftArrow,
    hideRightArrow,
    leftArrowClickHandler,
    rightArrowClickHandler,
    dateConfigForMonthToBeDisplayed,
  ];
}

interface ICalendarViewProps {
  renderDateItem: Function;
  monthToBeDisplayed: number;
  yearToBeDisplayed: number;
  minDateAllowed: Date;
  maxDateAllowed: Date;
}

export const CalendarView = (props: ICalendarViewProps) => {
  const {
    renderDateItem,
    monthToBeDisplayed,
    yearToBeDisplayed,
    minDateAllowed,
    maxDateAllowed,
  } = props;

  const tempDateObjToBeUsedForMonthToBeDisplayedArg = new Date();
  tempDateObjToBeUsedForMonthToBeDisplayedArg.setDate(1);
  tempDateObjToBeUsedForMonthToBeDisplayedArg.setMonth(monthToBeDisplayed);
  tempDateObjToBeUsedForMonthToBeDisplayedArg.setFullYear(yearToBeDisplayed);

  const [
    hideLeftArrow,
    hideRightArrow,
    leftArrowClickHandler,
    rightArrowClickHandler,
    dateConfigForMonthToBeDisplayed,
  ] = useCalendarView({
    dateConfigForMonthToBeDisplayedFromProps: tempDateObjToBeUsedForMonthToBeDisplayedArg,
    minDateAllowed,
    maxDateAllowed,
  });

  return (
    <S.CalendarWrapDiv>
      <S.CalendarMainComponentWrapper>
        <S.OuterMonthWrapDiv>
          <S.InnerMonthWrapDiv>
            <S.MonthChangeLeftArrowDiv
              onClick={e => {
                leftArrowClickHandler();
                e.stopPropagation();
              }}
            >
              <S.SliderArrow>
                <ArrowLeftIcon fill={(hideLeftArrow && '#accff9') || ''} />
              </S.SliderArrow>
            </S.MonthChangeLeftArrowDiv>

            <S.MonthNamePara>
              {dateFormat(dateConfigForMonthToBeDisplayed, 'mmmm yyyy')}
            </S.MonthNamePara>
            <S.MonthChangeRightArrowDiv
              onClick={e => {
                rightArrowClickHandler();
                e.stopPropagation();
              }}
            >
              <S.SliderArrow>
                <ArrowRightIcon fill={(hideRightArrow && '#accff9') || ''} />
              </S.SliderArrow>
            </S.MonthChangeRightArrowDiv>
          </S.InnerMonthWrapDiv>
        </S.OuterMonthWrapDiv>
        <S.CalendarMainWrapperDiv>
          <S.MonthWrapper>
            <S.DayAndDateWrapperDiv>
              <S.DayWrapDiv>
                {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map(item => (
                  <li key={item}>{item}</li>
                ))}
              </S.DayWrapDiv>
              <S.DateWrapDiv>
                <MonthView
                  renderDateItem={renderDateItem}
                  monthToBeDisplayed={dateConfigForMonthToBeDisplayed.getMonth()}
                  yearToBeDisplayed={dateConfigForMonthToBeDisplayed.getFullYear()}
                />
              </S.DateWrapDiv>
            </S.DayAndDateWrapperDiv>
          </S.MonthWrapper>
        </S.CalendarMainWrapperDiv>
      </S.CalendarMainComponentWrapper>
    </S.CalendarWrapDiv>
  );
};
